import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Sunset, MapPin, LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';

interface Feature {
  /** Unique identifier for the feature */
  id: string;
  /** Icon component to display */
  icon: LucideIcon;
  /** Feature title */
  title: string;
  /** Feature description */
  description: string;
}

interface WhyChooseUsProps {
  /** Additional CSS classes for styling */
  className?: string;
}

/**
 * WhyChooseUs Component
 * 
 * Displays a features showcase section highlighting the key benefits
 * of using the wildlife adventures platform. Extracted from HomePage
 * for better maintainability and reusability.
 */
export function WhyChooseUs({ 
  className = ""
}: WhyChooseUsProps) {
  const { t } = useTranslation('public', { keyPrefix: 'homepage.whyChooseUs' });

  // Default features data from translations
  const features: Feature[] = [
    {
      id: 'localExpertise',
      icon: Globe,
      title: t('features.localExpertise.title'),
      description: t('features.localExpertise.description')
    },
    {
      id: 'uniqueExperiences',
      icon: Sunset,
      title: t('features.uniqueExperiences.title'),
      description: t('features.uniqueExperiences.description')
    },
    {
      id: 'globalDestinations',
      icon: MapPin,
      title: t('features.globalDestinations.title'),
      description: t('features.globalDestinations.description')
    }
  ];

  return (
    <section className={`py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50/50 ${className}`}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent mb-4">
            {t('title')}
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('subtitle')}
          </p>
        </motion.div>
        
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.1,
                ease: "easeOut"
              }}
            >
              <Card className="h-full group hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 sm:p-8 text-center">
                  {/* Feature Icon with gradient background */}
                  <motion.div 
                    className={`w-16 h-16 mx-auto mb-6 rounded-xl ${
                      'bg-gradient-to-br from-action-500 to-action-600 shadow-lg shadow-action-500/25'
                    } flex items-center justify-center transition-transform duration-300 group-hover:scale-105`}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <feature.icon className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  {/* Feature Title */}
                  <h3 className="text-xl font-semibold mb-3 text-slate-900">
                    {feature.title}
                  </h3>
                  
                  {/* Feature Description */}
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
} 